// ═══════════════════════════════════════════════════════════════════
//  APPOINTMENTS → SEARCH: THE CRITERIA FORM, IN PURE FUNCTIONS
//
//  The form is checked here the way the server checks it
//  (AmsBookingSearchRules.Validate), so the Search button stays off until
//  the server would accept the search, and says why field by field:
//  - a Pacific date range, both ends required and inclusive, at most
//    MAX_RANGE_DAYS long, `from` on or before `to`;
//  - nothing else: every criterion is optional, and a range alone lists
//    every appointment in it, a page at a time (#770, 2026-10-09);
//  - an address with at least one word besides a unit marker or the state,
//    and at most ADDRESS_MAX_WORDS of them;
//  - a phone number, when given, that reduces to ten digits;
//  - an energy account number with something besides spaces and dashes,
//    and a confirmation number of six digits (#771);
//  - a Social Security number of nine digits that is neither an agency
//    dummy (9xx) nor one the SSA never issues (#771), offered only to
//    holders of appointments:reveal-ssn.
//
//  The dates travel as the Pacific calendar dates they are ("YYYY-MM-DD",
//  the server's DateOnly); the server turns them into instants. Nothing
//  here reads the browser's zone.
//
//  Adding a plain criterion is one entry in TEXT_CRITERIA plus its field in
//  SearchForm and SearchRequest: validation, the request builder and the
//  form's inputs all walk that list.
//
//  The Social Security number is deliberately NOT in that list. It is
//  masked, offered only with appointments:reveal-ssn, never put in
//  buildSearchRequest's body (whose result is the search query's cache key)
//  and sent only by buildSsnSearchRequest, through a mutation; see
//  ssnSearch.ts.
// ═══════════════════════════════════════════════════════════════════

import { SITE_TIME_ZONE, addDays, todayInZone } from "@/app/booking-calendar/lib/siteTime"
import { BookingRequestStatus, statusLabel } from "@/app/booking-calendar/lib/bookingStatus"
import type { BookingSearchRequest, BookingSsnSearchRequest } from "./searchApi"

/** The longest range the server answers for, both ends included (AmsQueryWindow.Max). */
export const MAX_RANGE_DAYS = 92

/** How far either side of today the form starts: past and future appointments alike, within the cap. */
export const DEFAULT_RANGE_DAYS_EACH_SIDE = 30

/** The page sizes offered. The server caps a page at 200 (AmsBookingSearchCriteria.MaxTake). */
export const PAGE_SIZES = [25, 50] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 25

/**
 * How many results a search by Social Security number asks for, in one page: it is an exact lookup,
 * and paging it would mean sending the number again. The server's most (AmsBookingSearchCriteria.MaxTake).
 */
export const SSN_SEARCH_TAKE = 200

/** The digits in a Social Security number. */
export const SSN_LENGTH = 9

/** The digits in a confirmation code (BookingConfirmationCode). */
export const CONFIRMATION_CODE_LENGTH = 6

/** The server's [StringLength] on the address (AmsBookingSearchRules.AddressMaxLength). */
export const ADDRESS_MAX_LENGTH = 200

/** The most words the server matches an address on (AmsBookingSearchRules.AddressMaxWords). */
export const ADDRESS_MAX_WORDS = 12

/** The permission a search by Social Security number needs on top of appointments:view. */
export const REVEAL_SSN_PERMISSION = "appointments:reveal-ssn"

/** The free-text criteria, in the order the form shows them. */
export type TextCriterion = "customerName" | "address" | "phoneNumber" | "email" | "referenceCode" | "energyAccountNumber" | "confirmationCode"

export interface TextCriterionDefinition {
  key: TextCriterion
  label: string
  /** The server's [StringLength] on the request field, so the input cannot overrun it. */
  maxLength: number
  inputType: "text" | "tel" | "email"
  inputMode?: "numeric"
  autoComplete: "off"
  placeholder?: string
  hint?: string
}

export const TEXT_CRITERIA: readonly TextCriterionDefinition[] = [
  { key: "customerName", label: "Customer name", maxLength: 200, inputType: "text", autoComplete: "off", hint: "Part of the first or last name." },
  { key: "address", label: "Address", maxLength: ADDRESS_MAX_LENGTH, inputType: "text", autoComplete: "off", hint: 'Part of the street address, e.g. "Tobin St" or a ZIP code.' },
  { key: "phoneNumber", label: "Phone number", maxLength: 30, inputType: "tel", autoComplete: "off", placeholder: "(206) 555-0100" },
  { key: "email", label: "Email", maxLength: 255, inputType: "email", autoComplete: "off", hint: "The whole address." },
  { key: "referenceCode", label: "Booking reference", maxLength: 64, inputType: "text", autoComplete: "off", hint: "The whole reference." },
  { key: "energyAccountNumber", label: "Energy account number", maxLength: 64, inputType: "text", autoComplete: "off", hint: "The whole number; spaces, dashes and case are ignored." },
  { key: "confirmationCode", label: "Confirmation number", maxLength: CONFIRMATION_CODE_LENGTH, inputType: "text", inputMode: "numeric", autoComplete: "off", hint: "Six digits." },
]

/**
 * Every status a booking request can hold, in the order staff read them — the numbers the server
 * stores and accepts. 8 is retired and is not offered.
 */
export const SEARCHABLE_STATUSES: readonly number[] = [
  BookingRequestStatus.Booked,
  BookingRequestStatus.Incomplete,
  BookingRequestStatus.Complete,
  BookingRequestStatus.NoShow,
  BookingRequestStatus.Cancelled,
  BookingRequestStatus.Rescheduled,
  BookingRequestStatus.VoluntaryWithdrawal,
  BookingRequestStatus.FailedToProvideDocs,
  BookingRequestStatus.Other,
  BookingRequestStatus.Invalid,
  BookingRequestStatus.SystemError,
  BookingRequestStatus.Screened,
  BookingRequestStatus.Refused,
  BookingRequestStatus.Abandoned,
  BookingRequestStatus.Expired,
]

export interface SearchForm extends Record<TextCriterion, string> {
  /** "YYYY-MM-DD", a Pacific calendar date, inclusive. */
  from: string
  /** "YYYY-MM-DD", a Pacific calendar date, inclusive. */
  to: string
  statuses: number[]
  /**
   * A Social Security number, as typed. Only ever set when the user holds appointments:reveal-ssn,
   * held only until Search is chosen — the page clears it as it sends — and sent only by
   * buildSsnSearchRequest.
   */
  ssn: string
}

/** Whether the user may search by Social Security number, which changes what the form offers. */
export interface SearchFormOptions {
  ssnAllowed?: boolean
}

/** The fields a message can belong to; `range` is the from–to pair taken together. */
export type SearchFormField = TextCriterion | "ssn" | "from" | "to" | "range"

export interface SearchFormValidation {
  valid: boolean
  errors: Partial<Record<SearchFormField, string>>
}

/** An empty form over the default range: the last and next DEFAULT_RANGE_DAYS_EACH_SIDE Pacific days. */
export function defaultSearchForm(now: Date = new Date(), timeZone: string = SITE_TIME_ZONE): SearchForm {
  const today = todayInZone(timeZone, now)
  return {
    from: addDays(today, -DEFAULT_RANGE_DAYS_EACH_SIDE),
    to: addDays(today, DEFAULT_RANGE_DAYS_EACH_SIDE),
    customerName: "",
    address: "",
    phoneNumber: "",
    email: "",
    referenceCode: "",
    energyAccountNumber: "",
    confirmationCode: "",
    statuses: [],
    ssn: "",
  }
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A "YYYY-MM-DD" as a day number, or undefined when it is not a real date. Calendar arithmetic, in UTC. */
function dayNumber(value: string): number | undefined {
  const match = CALENDAR_DATE.exec(value.trim())
  if (!match) return undefined
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return undefined
  return date.getTime() / 86_400_000
}

/** How many calendar days `from`..`to` covers, both ends included; undefined unless both are dates. */
export function daysCovered(from: string, to: string): number | undefined {
  const start = dayNumber(from)
  const end = dayNumber(to)
  return start === undefined || end === undefined ? undefined : end - start + 1
}

/**
 * Ten digits, no country code — the server's BookingPhoneNumber.Normalize — or undefined when the
 * number would not match anything (too short, too long, or one digit repeated, which is a placeholder).
 */
export function normalizePhone(value: string): string | undefined {
  let digits = value.replace(/\D/g, "")
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
  if (digits.length !== 10) return undefined
  if (/^(\d)\1*$/.test(digits)) return undefined
  return digits
}

/**
 * The nine digits of a Social Security number — the server's AmsBookingSearchRules.SsnDigits — or
 * undefined when it is not one: only digits, spaces and dashes, and exactly nine digits.
 */
export function ssnDigits(value: string): string | undefined {
  const trimmed = value.trim()
  if (!/^[0-9 -]+$/.test(trimmed)) return undefined
  const digits = trimmed.replace(/[^0-9]/g, "")
  return digits.length === SSN_LENGTH ? digits : undefined
}

/**
 * Why the server would refuse this Social Security number, or undefined when it would search it.
 * None of these messages quotes what was typed.
 */
export function ssnProblem(value: string): string | undefined {
  const digits = ssnDigits(value)
  if (digits === undefined) return "Enter the nine digits of the Social Security number."
  // Agency dummy numbers (900–999) are shared by many households, so they are not searched (#771).
  if (digits.startsWith("9")) return "Agency dummy numbers (starting with 9) cannot be searched."
  const area = Number(digits.slice(0, 3))
  const group = Number(digits.slice(3, 5))
  const serial = Number(digits.slice(5))
  if (area === 0 || area === 666 || group === 0 || serial === 0) return "That is not a number the SSA issues, so it cannot be searched."
  return undefined
}

/** The energy account number as the server compares it: spaces and dashes removed, upper-cased. */
export function normalizeEnergyAccountNumber(value: string): string {
  return value.trim().replace(/[ -]/g, "").toUpperCase()
}

function hasText(value: string): boolean {
  return value.trim().length > 0
}

/** Whether the form names a Social Security number the user may search by. */
export function isSsnSearch(form: SearchForm, options: SearchFormOptions = {}): boolean {
  return !!options.ssnAllowed && hasText(form.ssn)
}

/**
 * The unit markers the server drops from an address before matching (BookingAddressNormalizer's
 * UnitDesignators): "Apt 4B" and "#4B" both search for 4B.
 */
const UNIT_MARKERS = new Set([
  "APT", "APARTMENT", "UNIT", "STE", "SUITE", "RM", "ROOM", "BLDG", "BLD", "BUILDING",
  "SPC", "SP", "SPACE", "TRLR", "TRAILER", "LOT", "FL", "FLR", "FLOOR", "#",
])

/**
 * The state words the server drops from an address before matching (BookingAddressNormalizer's
 * StateTokens): every address is in Washington, and many are stored without it.
 */
const STATE_WORDS = new Set(["WA", "WASH", "WASHINGTON"])

/**
 * The words the server would match an address on, as BookingAddressNormalizer.SearchTokens splits
 * them: letters and digits (a hyphen inside a word kept), `#` on its own, everything else a
 * separator (parentheses and apostrophes included), unit markers and state words dropped, repeats
 * dropped. Only counted here; the server does the matching, abbreviations included.
 */
export function addressWords(value: string): string[] {
  const words = (value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().match(/#|[A-Z0-9][A-Z0-9-]*/g) ?? [])
    .map(word => word.replace(/-+$/, ""))
    .filter(word => word.length > 0 && !UNIT_MARKERS.has(word) && !STATE_WORDS.has(word))
  return [...new Set(words)]
}

/** Checks the form as the server would, with a message per field that is wrong. */
export function validateSearchForm(form: SearchForm, options: SearchFormOptions = {}): SearchFormValidation {
  const errors: SearchFormValidation["errors"] = {}

  const from = dayNumber(form.from)
  const to = dayNumber(form.to)
  if (from === undefined) errors.from = "Choose the first date to search."
  if (to === undefined) errors.to = "Choose the last date to search."

  if (from !== undefined && to !== undefined) {
    if (to < from) {
      errors.range = "The last date must be on or after the first."
    } else if (to - from + 1 > MAX_RANGE_DAYS) {
      errors.range = `The date range must be no longer than ${MAX_RANGE_DAYS} days.`
    }
  }

  if (hasText(form.address)) {
    const words = addressWords(form.address).length
    if (words === 0) errors.address = "Enter part of the address: a street, number or ZIP code."
    else if (words > ADDRESS_MAX_WORDS) errors.address = `Enter no more than ${ADDRESS_MAX_WORDS} words of the address.`
  }

  if (hasText(form.phoneNumber) && normalizePhone(form.phoneNumber) === undefined) {
    errors.phoneNumber = "Enter a ten-digit phone number."
  }

  if (hasText(form.energyAccountNumber) && normalizeEnergyAccountNumber(form.energyAccountNumber) === "") {
    errors.energyAccountNumber = "Enter the account number's letters or digits."
  }

  if (hasText(form.confirmationCode) && !/^[0-9]{6}$/.test(form.confirmationCode.trim())) {
    errors.confirmationCode = "Enter the six-digit confirmation number."
  }

  if (isSsnSearch(form, options)) {
    const problem = ssnProblem(form.ssn)
    if (problem) errors.ssn = problem
  }

  return { valid: Object.keys(errors).length === 0, errors }
}

/**
 * The body of `POST api/ams/booking-requests/search`: the range as given, every text criterion
 * trimmed and left out when empty, the statuses as the numbers the server stores, and the page.
 * Never the Social Security number, whatever the form holds: this body is the search query's
 * cache key. A search by one is built by buildSsnSearchRequest.
 */
export function buildSearchRequest(form: SearchForm, page = 0, pageSize: number = DEFAULT_PAGE_SIZE): BookingSearchRequest {
  const request: BookingSearchRequest = {
    from: form.from.trim(),
    to: form.to.trim(),
    skip: Math.max(0, page) * pageSize,
    take: pageSize,
  }
  for (const { key } of TEXT_CRITERIA) {
    const value = form[key].trim()
    if (value) request[key] = value
  }
  if (form.statuses.length > 0) {
    request.statuses = [...new Set(form.statuses)].sort((a, b) => a - b)
  }
  return request
}

/**
 * The body of a search by Social Security number, sent only through the `searchBookingRequestsBySsn`
 * mutation: the rest of the criteria as buildSearchRequest builds them, the number as its nine digits,
 * and one page of SSN_SEARCH_TAKE, so nothing ever needs to send it again to page.
 */
export function buildSsnSearchRequest(form: SearchForm): BookingSsnSearchRequest {
  return { ...buildSearchRequest(form, 0, SSN_SEARCH_TAKE), ssn: ssnDigits(form.ssn) ?? form.ssn.trim() }
}

/** What the status picker's button says. */
export function statusSummary(statuses: readonly number[]): string {
  if (statuses.length === 0) return "Any status"
  if (statuses.length <= 2) return statuses.map(statusLabel).join(", ")
  return `${statuses.length} statuses`
}

/** The statuses with `status` switched on or off. */
export function toggleStatus(statuses: readonly number[], status: number, on: boolean): number[] {
  const rest = statuses.filter(value => value !== status)
  return on ? [...rest, status] : rest
}
